import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, PageHeader, Skeleton, toast } from "@optymyse/suite-ui";
import { useCompany, useUpdateCompany } from "../lib/directory";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, Page, Panel } from "../components/directory/Page";
import { changeOf, CompanyForm, draftOf, type FormProblem } from "../components/directory/CompanyForm";

/**
 * A customer's change, made to the version the page read (If-Match): a customer someone changed since is refused, and
 * the page says so and shows it as it is now, so nobody's change is lost to another's.
 */
function EditCustomerPage() {
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const company = useCompany(companyId);
  const update = useUpdateCompany(companyId);
  const [problem, setProblem] = useState<FormProblem | null>(null);
  const [conflict, setConflict] = useState(false);
  const back = () => void navigate({ to: "/admin/customers/$companyId", params: { companyId } });

  if (company.isPending)
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </Page>
    );
  if (company.isError)
    return (
      <Page>
        <DirectoryProblem
          {...(company.error instanceof ApiError ? { message: company.error.message } : {})}
          onRetry={() => void company.refetch()}
        />
      </Page>
    );

  const record = company.data;
  return (
    <Page>
      <PageHeader
        breadcrumb={[
          { label: strings.customers.title, href: "/admin/customers" },
          { label: record.display_name, href: `/admin/customers/${companyId}` },
          { label: strings.customers.page.edit },
        ]}
        title={fill(strings.customers.form.editTitle, { name: record.display_name })}
      />
      {conflict && (
        <Banner tone="warning" title={strings.customers.form.conflictTitle} role="alert">
          {strings.customers.form.conflict}
        </Banner>
      )}
      <Panel>
        <CompanyForm
          // The customer as read: a newer version, after a refusal, starts the form again from it.
          key={record.version}
          initial={draftOf(record)}
          submitLabel={strings.customers.form.save}
          pending={update.isPending}
          problem={problem}
          onCancel={back}
          onSubmit={(draft) => {
            const change = changeOf(record, draft);
            if (Object.keys(change).length === 0) {
              setProblem({ message: strings.customers.form.nothingChanged, field: null });
              return;
            }
            setProblem(null);
            setConflict(false);
            update.mutate(
              { change, version: record.version },
              {
                onSuccess: (saved) => {
                  toast({ title: fill(strings.customers.form.saved, { name: saved.display_name }) });
                  back();
                },
                onError: (error) => {
                  if (error instanceof ApiError && error.status === 412) {
                    setConflict(true);
                    return;
                  }
                  setProblem({ message: error.message, field: error instanceof ApiError ? error.field : null });
                },
              },
            );
          }}
        />
      </Panel>
    </Page>
  );
}

export const Route = createFileRoute("/admin/customers/$companyId/edit")({ component: EditCustomerPage });
